/*
 * Utility module
 */

/**
 * @deprecated
 * @type {{extend: (function(Object, ...[Object]): Object), bind: (function(Function, Object, *=): function(...[*]): any), trim: (function(string): string), removeClass: lib.removeClass, hasClass: (function(HTMLElement, string): *), event: {add: lib.event.add, remove: lib.event.remove}, each: lib.each, addClass: lib.addClass, queryElementsBySelector: ((function(string, (HTMLElement|Document)=): (NodeList|Array))|*)}}
 */
const lib = {
	/**
	 * Checks if the specified element has the given class.
	 *
	 * @param {HTMLElement} el - The element to check.
	 * @param {string} cls - The class name to look for.
	 * @returns {boolean} - Returns true if the element has the class, otherwise false.
	 */
	hasClass: (el, cls) =>
		el instanceof HTMLElement && el.classList.contains(cls),
	/**
	 * Adds the specified class to the element if it does not already have it.
	 *
	 * @param {HTMLElement} el - The element to which the class will be added.
	 * @param {string} cls - The class name to add.
	 */
	addClass: (el, cls) => {
		if (el instanceof HTMLElement) el.classList.add(cls);
	},
	/**
	 * Removes the specified class from the element if it has it.
	 *
	 * @param {HTMLElement} el - The element from which the class will be removed.
	 * @param {string} cls - The class name to remove.
	 */
	removeClass: (el, cls) => {
		if (el instanceof HTMLElement) el.classList.remove(cls);
	},
	/**
	 * Extends the target object with properties from one or more source objects.
	 *
	 * @param {Object} obj - The target object to extend.
	 * @param {...Object} sources - One or more source objects containing properties to copy.
	 * @returns {Object} - The extended target object.
	 */
	extend: (obj, ...sources) => {
		for (const source of sources) {
			for (const key of Object.keys(source)) {
				if (Object.prototype.hasOwnProperty.call(source, key)) {
					obj[key] = source[key];
				}
			}
		}
		return obj;
	},
	each: (obj, callback) => {
		if (Array.isArray(obj)) {
			for (let i = 0; i < obj.length; i++) {
				if (callback.call(obj[i], i, obj[i]) === false) {
					break;
				}
			}
		} else {
			for (const property in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, property)) {
					if (callback.call(obj[property], property, obj[property]) === false) {
						break;
					}
				}
			}
		}
	},
	event: (() => {
		/**
		 * Fixes the event object to ensure cross-browser compatibility.
		 *
		 * @param {Event} e - The event object to fix.
		 * @returns {Event} - The fixed event object.
		 */
		const fixEvent = (e = window.event) => {
			if (e.isFixed) return e;
			e.isFixed = true;
			e.target = e.target || e.srcElement;
			e.preventDefault =
				e.preventDefault ||
				function () {
					this.returnValue = false;
				};
			e.stopPropagation =
				e.stopPropagation ||
				function () {
					this.cancelBubble = true;
				};
			return e;
		};

		return {
			/**
			 * Adds an event listener to the specified element and ensures compatibility with older browsers.
			 *
			 * @param {HTMLElement} elem - The element to which the event listener will be added.
			 * @param {string} event - The type of event to listen for (e.g., 'click', 'mouseover').
			 * @param {Function} handler - The function to execute when the event is triggered.
			 */
			add: (elem, event, handler) => {
				// Initialize the `events` property on the element if it doesn't exist
				if (!elem.events) {
					elem.events = {};

					// Create a unified event handler for the element
					elem.handle = (e) => {
						// Fix the event object for cross-browser compatibility
						const _e = fixEvent(e);

						// Iterate over all handlers for the event type and execute them
						for (const handler of elem.events[_e.type]) {
							const ret = handler.call(elem, _e);

							// If the handler returns `false`, prevent default behavior and stop propagation
							if (ret === false) {
								_e.preventDefault();
								_e.stopPropagation();
							}
						}
					};
				}

				// Initialize the array of handlers for the specific event type if it doesn't exist
				if (!elem.events[event]) {
					elem.events[event] = [];

					// Add the unified event handler to the element using the appropriate method
					if (elem.addEventListener) {
						elem.addEventListener(event, elem.handle, false);
					} else if (elem.attachEvent) {
						elem.attachEvent(`on${event}`, elem.handle);
					}
				}

				// Add the provided handler to the list of handlers for the event type
				elem.events[event].push(handler);
			},
			/**
			 * Removes the specified event handler from the element.
			 *
			 * @param {HTMLElement} elem - The element from which the event handler will be removed.
			 * @param {string} event - The event type to remove.
			 * @param {Function} handler - The event handler function to remove.
			 */
			remove: (elem, event, handler) => {
				const handlers = elem.events[event];
				const index = handlers.indexOf(handler);
				if (index !== -1) handlers.splice(index, 1);
				if (!handlers.length) {
					delete elem.events[event];
					if (elem.removeEventListener)
						elem.removeEventListener(event, elem.handle, false);
					else if (elem.detachEvent)
						elem.detachEvent(`on${event}`, elem.handle);
				}
			},
		};
	})(),

	/**
	 * Queries elements by a CSS selector within a specified scope.
	 *
	 * @param {string} selector - The CSS selector to query elements.
	 * @param {HTMLElement|Document} [scope=document] - The scope within which to query elements.
	 * @returns {NodeList|Array} - The list of elements matching the selector.
	 */
	queryElementsBySelector: (selector, scope = document) => {
		if (!selector) return [];
		if (selector === '>*') return scope.children;
		if (typeof document.querySelectorAll === 'function') {
			return scope.querySelectorAll(selector);
		}

		const selectors = selector.split(',');

		let resultList = [];

		for (const sel of selectors) {
			let currentContext = [scope];
			const tokens = sel.trim().split(' ');

			for (const token of tokens) {
				const trimmedToken = token.trim();

				if (trimmedToken.includes('#')) {
					const [tagName, id] = trimmedToken.split('#');
					const element = document.getElementById(id);
					if (
						element &&
						tagName &&
						element.nodeName.toLowerCase() !== tagName
					) {
						return [];
					}
					currentContext = element ? [element] : [];
					continue;
				}

				if (trimmedToken.includes('.')) {
					const [tagName = '*', className] = trimmedToken.split('.');
					let found = [];
					for (const ctx of currentContext) {
						const elements = Array.from(ctx.getElementsByTagName(tagName));
						found = [...found, ...elements];
					}
					currentContext = found.filter((el) =>
						el.classList.contains(className)
					);
					continue;
				}

				const attrMatch = trimmedToken.match(
					/^(\w*)\[(\w+)([=~|^$*]?)="?([^\]"]*)"?\]$/
				);
				if (attrMatch) {
					const [_, tagName = '*', attrName, attrOperator, attrValue] =
						attrMatch;
					let found = [];
					for (const ctx of currentContext) {
						const elements = Array.from(ctx.getElementsByTagName(tagName));
						found = [...found, ...elements];
					}

					const checkFunction = (el) => {
						const attr = el.getAttribute(attrName);
						switch (attrOperator) {
							case '=':
								return attr === attrValue;
							case '~':
								return attr?.split(' ').includes(attrValue);
							case '|':
								return attr?.startsWith(`${attrValue}-`);
							case '^':
								return attr?.startsWith(attrValue);
							case '$':
								return attr?.endsWith(attrValue);
							case '*':
								return attr?.includes(attrValue);
							default:
								return attr !== null;
						}
					};

					currentContext = found.filter(checkFunction);
					continue;
				}

				let found = [];
				for (const ctx of currentContext) {
					const elements = Array.from(ctx.getElementsByTagName(trimmedToken));
					found = [...found, ...elements];
				}
				currentContext = found;
			}

			resultList = [...resultList, ...currentContext];
		}

		return resultList;
	},
	/**
	 * Trims leading and trailing whitespace from the given string.
	 *
	 * @param {string} str - The string to trim.
	 * @returns {string} - The trimmed string.
	 */
	trim: (str) => str.replace(/^\s+/, '').replace(/\s+$/, ''),
	/**
	 * Binds a function to a specific scope and optionally forces arguments.
	 *
	 * @param {Function} f - The function to bind.
	 * @param {Object} scope - The scope to bind the function to.
	 * @param {*} [forceArgs] - Optional arguments to force when the function is called.
	 * @returns {Function} - The bound function.
	 */
	bind:
		(f, scope, forceArgs) =>
		(...args) =>
			f.apply(scope, forceArgs !== undefined ? [forceArgs] : args),
};

export default lib;
